import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShoppingCart, Upload, Zap } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { captureProof } from "@/lib/proof";
import { themeOf } from "@/lib/mall";
import { useCart } from "@/context/CartContext";
import { StoreButton } from "@/components/Buttons";
import DesignerBar from "@/components/signs/DesignerBar";
import { useSimpleLine } from "@/components/simple/product/useSimpleLine";
import { FileCheck, NoteField, ReadyBy, OpeningSoon, dpiCheck, money } from "@/components/simple/product/Fields";
import ProofStage, { emptySides, uploadSide } from "./PrintDesigner";
import { parseSize } from "./mockups/Mockup";

const WALL = "#eef0f3";
const code = (s) => String(s).replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
const fmt = (s) => (/^\d+$/.test(String(s)) ? Number(s).toLocaleString() : String(s));
const FOLDS = { "Half Fold": 2, "Tri-Fold": 3, "Z-Fold": 3, "Gate Fold": 3, "Accordion-4 Fold": 4, "French Fold": 4, "Roll Fold": 4, "Double Parallel Fold": 4, "Parallel Map Fold": 4, "Double Gate Fold": 4 };
const labelFor = (labels, re) => Object.entries(labels || {}).find(([k]) => re.test(k))?.[1] || "";
const select = "min-h-[40px] w-full border border-zinc-300 bg-white px-2.5 text-[14px] text-zinc-900 focus:border-zinc-900 focus:outline-none";

/** One NDF attribute, laid out the way NDF lays it out: the label on the left, one dropdown on the right. */
function Field({ f, onChange }) {
    const key = code(f.name);
    const one = f.options.length === 1;
    return (
        <div className="grid grid-cols-[112px_1fr] items-center gap-3 border-b border-zinc-100 py-2" data-testid={`print-field-${key}`}>
            <span className="text-[13px] font-semibold text-zinc-800">{f.name}:</span>
            {one
                ? <span className="text-[14px] text-zinc-700" data-testid={`print-fixed-${key}`}>{fmt(f.options[0].label)}</span>
                : (
                    <select value={f.value} onChange={(e) => onChange(e.target.value)} className={select} data-testid={`print-select-${key}`}>
                        {f.options.map((o) => <option key={o.id} value={o.id}>{fmt(o.label)}</option>)}
                    </select>
                )}
        </div>
    );
}

/** NDF's turnaround block: every option with its own total and ready date, the chosen one marked. */
function Turnaround({ f, turnarounds, onChange, theme }) {
    const rows = turnarounds.length ? turnarounds : f.options.map((o) => ({ ...o, price: null }));
    return (
        <div className="py-3" data-testid="print-turnaround">
            <p className="text-[13px] font-semibold text-zinc-800">{f.name}</p>
            <div className="mt-2 grid gap-1.5">
                {rows.map((t) => {
                    const on = t.id === f.value;
                    return (
                        <button key={t.id} type="button" role="radio" aria-checked={on} onClick={() => onChange(t.id)} data-testid={`print-turnaround-${code(t.label)}`}
                            className="flex items-center gap-3 border px-3 py-2 text-left transition-colors" style={on ? { borderColor: theme.primary, background: "#fff" } : { borderColor: "#e4e4e7" }}>
                            <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border" style={{ borderColor: on ? theme.primary : "#a1a1aa" }}>
                                {on && <span className="h-2 w-2 rounded-full" style={{ background: theme.primary }} />}
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block text-[14px] font-semibold text-zinc-900">{t.label}</span>
                                {t.ready?.text && <span className="block text-[12px] text-zinc-500">{t.ready.text}{/today/i.test(t.label) && !/today/i.test(t.ready.text) ? " · past today's cutoff" : ""}</span>}
                            </span>
                            {t.price != null && <span className="font-mono text-[13px] font-bold tabular-nums text-zinc-900">{money(t.price)}</span>}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * The Flyers & Print designer (Phase 10): the shared designer bar on top, NDF's fields in NDF's order down the left,
 * the proof — the customer's own file on the product — filling the rest of the screen. Every change asks the server
 * for NDF's price of that exact selection (× platform % × shop markup).
 */
export default function PrintBody({ tile, variant, onVariant, store, cleanupFee }) {
    const v = variant || tile.variants[0];
    const theme = themeOf(store);
    const line = useSimpleLine({ store, cleanupFee });
    const navigate = useNavigate();
    const { setOpen: openCart } = useCart();
    const [sel, setSel] = useState(() => ({ ...(v.ndf_defaults || {}) }));
    const [q, setQ] = useState(null);
    const [busy, setBusy] = useState(true);
    const [qErr, setQErr] = useState("");
    const [sides, setSides] = useState(emptySides);
    const [view, setView] = useState("front");
    const seq = useRef(0);
    const stageRef = useRef(null);
    const fileRef = useRef(null);
    const target = useRef("front");   // which side the next picked file lands on
    const pickFile = (s) => { target.current = s; if (s !== "front") setView(s); fileRef.current?.click(); };
    const selKey = JSON.stringify(sel);
    useEffect(() => { setSel({ ...(v.ndf_defaults || {}) }); setQ(null); }, [v.slug]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const n = ++seq.current;
        setBusy(true); setQErr("");
        const t = setTimeout(() => {
            api.post("/print/quote", { sku: v.sku, store: store?.slug, selection: sel })
                .then(({ data }) => {
                    if (n !== seq.current) return;
                    setQ(data); setBusy(false);
                    if (!Object.entries(data.selection).every(([k, val]) => sel[k] === val)) setSel((s) => ({ ...s, ...data.selection }));
                })
                .catch((e) => {
                    if (n !== seq.current) return;
                    setBusy(false); setQErr(e.response?.data?.detail || "Pricing is taking a moment — try again.");
                });
        }, 150);
        return () => clearTimeout(t);
    }, [v.sku, store?.slug, selKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const labels = q?.labels || {};
    const sizeValue = labels.Size || labelFor(labels, /graphic size|^size|paper$|sticker size/i) || labelFor(labels, /size/i);
    const back = labelFor(labels, /back side|printed side|print side|sides/i);
    const twoSided = v.sides === 2 && !!back && !/no printing|front only|single/i.test(back);
    const which = twoSided ? view : "front";
    const [wIn] = parseSize(sizeValue);
    const front = sides.front;
    const check = useMemo(() => dpiCheck(front.file, wIn), [front.file, wIn]);
    useEffect(() => { if (check && !check.ok) line.setCleanup(true); if (check?.ok) line.setCleanup(false); }, [check?.ok]); // eslint-disable-line react-hooks/exhaustive-deps
    const foldLabel = labelFor(labels, /fold/i);
    const folds = FOLDS[Object.keys(FOLDS).find((k) => foldLabel.startsWith(k))] || (tile.mockup === "folded" ? 3 : 1);
    const win = /window/i.test(labelFor(labels, /paper|window|envelope/i));
    const total = (q?.price || 0) + line.fee;
    const errors = [!front.file && "Upload your file", twoSided && front.file && !sides.back.file && "Upload the back (or use the front image)", qErr].filter(Boolean);
    const fields = q?.fields || [];
    const taField = fields.find((f) => f.kind === "turnaround");
    const pick = (id, val) => setSel((s) => ({ ...s, [id]: val }));
    const openingSoon = store && store.accepts_cards === false;
    const blocked = line.busy || busy || !q || errors.length > 0 || openingSoon;
    const useFront = () => setSides((s) => ({ ...s, back: { ...s.front } }));

    const build = async () => {
        const proof = await captureProof(stageRef.current, `${v.sku}-proof`, WALL);
        return {
            product_slug: v.sku, product_name: v.variant !== v.name ? `${v.name} · ${v.variant}` : v.name,
            image: proof ? mediaUrl(proof.url) : (v.image ? mediaUrl(v.image) : null), proof,
            quantity: 1, unit_price: q.price, line_total: q.price, summary: q.summary, store,
            selections: {
                kind: "print", store_slug: store?.slug || v.store_slug, ready_in: q.ready_in, quantity: 1,
                print: { config: v.slug, variant: v.variant, selection: q.selection, labels: q.labels, pieces: q.qty, days: q.days,
                         turnaround: labelFor(q.labels, /ship|turnaround/i) },
                design_json: { sides: { front: { pan: sides.front.pan, fit: sides.front.fit }, back: { pan: sides.back.pan, fit: sides.back.fit } }, mockup: tile.mockup, size: sizeValue },
                art_transform: { ...sides.front.pan, fit: sides.front.fit },
            },
            artwork: [sides.front.file, twoSided ? sides.back.file : null].filter(Boolean),
        };
    };

    const fileName = (s) => s.file?.filename || s.file?.name || s.name || "";
    // one line per configuration: Add to cart then Pay now must not charge the same job twice
    const lineKey = JSON.stringify([v.sku, store?.slug, sel, sides.front.file?.url, sides.back.file?.url, line.note, line.needBy, line.cleanup]);
    const checkout = `/checkout/${store?.slug || v.store_slug}`;
    const addToCart = () => (line.inCart(lineKey) ? (toast("That's already in your cart"), openCart(true)) : line.submit(build, "drawer", lineKey));
    const payNow = () => (line.inCart(lineKey) ? navigate(checkout) : line.submit(build, "pay", lineKey));

    return (
        <div className="flex flex-col bg-white lg:h-[calc(100vh-148px)] lg:overflow-hidden" data-testid="print-page" data-tile={tile.key} data-variant={v.slug}>
            <DesignerBar back={tile.on_signs ? "/signs" : "/print"} backLabel={tile.on_signs ? "Signs" : "Flyers & Print"} backTestid="product-back"
                title={v.variant !== v.name ? `${v.name} · ${v.variant}` : v.name} titleTestid="product-title" description={q?.summary || ""} store={store} theme={theme} prefix="print"
                price={total} priceNote={q ? (q.qty > 1 ? `${money(q.unit_price)} each · ${fmt(q.qty)} pieces` : q.ready?.text) : ""} errors={errors}
                busy={busy || !q} blocked={blocked} onAdd={addToCart} adding={line.busy} />

            <div className="grid min-h-0 flex-1 lg:grid-cols-[400px_1fr]">
                <aside className="order-2 min-h-0 border-t border-zinc-200 bg-white lg:order-1 lg:overflow-y-auto lg:border-r lg:border-t-0" data-testid="print-options">
                    {tile.variants.length > 1 && (
                        <div className="border-b border-zinc-200 px-4 py-3" data-testid="print-variants">
                            <p className="text-[13px] font-semibold text-zinc-800">Product:</p>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                                {tile.variants.map((x) => (
                                    <button key={x.slug} type="button" onClick={() => onVariant(x.slug)} data-testid={`print-variant-${x.slug}`} aria-pressed={x.slug === v.slug}
                                        className="border px-3 py-2 text-[13px] font-semibold" style={x.slug === v.slug ? { borderColor: theme.primary, color: theme.ink || theme.primary } : { borderColor: "#e4e4e7", color: "#3f3f46" }}>
                                        {x.variant}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="px-4 pt-2" data-testid="print-fields" data-busy={busy ? "1" : "0"}>
                        {!q && !qErr && <p className="py-6 text-[14px] text-zinc-500" data-testid="print-fields-loading">Loading options…</p>}
                        {fields.filter((f) => f.kind !== "turnaround").map((f) => <Field key={f.id} f={f} onChange={(val) => pick(f.id, val)} />)}
                        {taField && <Turnaround f={taField} turnarounds={q?.turnarounds || []} onChange={(val) => pick(taField.id, val)} theme={theme} />}
                        {qErr && <p className="py-2 text-[13px] font-semibold text-red-600" data-testid="print-quote-error">{qErr}</p>}
                    </div>

                    <div className="border-t border-zinc-200 bg-zinc-50 px-4 py-3" data-testid="print-price-head">
                        <p className="flex items-baseline justify-between gap-3">
                            <span className="text-[13px] font-semibold text-zinc-800">Printing cost:</span>
                            <span className="font-mono text-[22px] font-black tabular-nums" style={{ color: theme.ink || theme.primary }} data-testid="print-panel-price">
                                {q ? money(q.price) : "…"}{busy && q ? <Loader2 size={14} className="ml-2 inline animate-spin" /> : null}
                            </span>
                        </p>
                        {q && q.qty > 1 && <p className="text-right text-[12px] text-zinc-500" data-testid="print-unit-price">( {money(q.unit_price)} for each · {fmt(q.qty)} pieces )</p>}
                        {line.fee > 0 && <p className="text-right text-[12px] text-zinc-500">+ {money(line.fee)} artwork cleanup</p>}
                        <p className="mt-1 text-[13px] font-semibold text-zinc-900" data-testid="print-ready">{q?.ready?.text || v.ready?.text}</p>
                    </div>

                    <div className="border-t border-zinc-200 px-4 py-3" data-testid="print-file-row">
                        <p className="text-[13px] font-semibold text-zinc-800">Your file{twoSided ? "s" : ""}:</p>
                        {["front", ...(twoSided ? ["back"] : [])].map((s) => (
                            <div key={s} className="mt-1.5 flex items-center justify-between gap-3 text-[13px]" data-testid={`print-file-${s}`}>
                                <span className="min-w-0 truncate text-zinc-700">{twoSided ? <span className="font-semibold capitalize">{s}: </span> : null}{fileName(sides[s]) || <span className="text-zinc-400">no file yet</span>}</span>
                                <button type="button" onClick={() => pickFile(s)} className="shrink-0 inline-flex items-center gap-1 font-semibold underline" data-testid={`print-upload-${s}`}>
                                    <Upload size={12} /> {sides[s].file ? "Replace" : "Upload"}
                                </button>
                            </div>
                        ))}
                        {twoSided && sides.front.file && !sides.back.file && (
                            <button type="button" onClick={useFront} className="mt-2 text-[12px] font-semibold underline" data-testid="use-front-image-row">Use the front image on the back</button>
                        )}
                    </div>
                    <FileCheck check={check} cleanup={line.cleanup} setCleanup={line.setCleanup} fee={cleanupFee} />
                    <NoteField value={line.note} onChange={line.setNote} placeholder={v.note_placeholder} />
                    <ReadyBy store={store} readyIn={q?.ready_in || v.ready_in} extraDays={line.extraDays} fallback={q?.ready || v.ready} needBy={line.needBy} setNeedBy={line.setNeedBy} />

                    <div className="border-t border-zinc-200 px-4 py-4" data-testid="print-actions">
                        {openingSoon ? <OpeningSoon store={store} /> : (
                            <>
                                {errors.length > 0 && <p className="mb-2 text-[12px] font-semibold text-red-600" data-testid="print-action-errors">{errors.join(" · ")}</p>}
                                <StoreButton theme={theme} onClick={payNow} disabled={blocked} className="w-full py-3.5" data-testid="print-pay-now">
                                    {line.busy ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />} Pay now · {money(total)}
                                </StoreButton>
                                <button type="button" onClick={addToCart} disabled={blocked} data-testid="print-add-to-cart-panel"
                                    className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border border-zinc-900 py-3 text-sm font-bold uppercase tracking-wide text-zinc-900 hover:bg-ink hover:text-[#fff] disabled:opacity-50">
                                    <ShoppingCart size={15} /> Add to cart
                                </button>
                            </>
                        )}
                    </div>
                </aside>

                <div className="relative order-1 h-[52vh] min-h-[360px] lg:order-2 lg:h-auto lg:min-h-0" data-testid="print-stage-wrap">
                    <ProofStage sides={sides} view={view} setView={setView} twoSided={twoSided} mockup={tile.mockup} size={sizeValue} window={win} folds={folds}
                        stageRef={stageRef} theme={theme} onUpload={() => pickFile(which)} onUseFront={useFront} />
                </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*,.pdf,.ai,.eps,.svg" className="hidden" data-testid="upload-input"
                onChange={(e) => { uploadSide(setSides, target.current, e.target.files?.[0]); e.target.value = ""; }} />
        </div>
    );
}
